import type { Ref } from 'react';
import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '~/components/ui/EmptyState';
import { RetryBanner } from '~/components/ui/RetryBanner';
import {
  calculateOrderTotal,
  formatOrderDisplayId,
  formatNameWithCode,
  sellerLabel,
  type PricedAmount,
} from '~/utils';
import type { Quantity, TradePerspective } from '~/graphql/graphql';
import { OrderCard } from '~/pages/orders/sections/OrderCard.tsx';
import { useNavigate } from 'react-router';
import { SellerOrderCard } from '~/pages/orders/sections/SellerOrderCard.tsx';
import { OrdersCountLine } from '~/pages/orders/sections/OrdersCountLine';
import type { OrderCardProduct } from '~/pages/orders/lib/orderCardProducts';
import { CopySkippedItemsModal } from '~/components/overlays';
import { useCopyOrderToCartAction } from '~/hooks/useCopyOrderToCartAction';

type OrderListOrder = {
  id: string;
  purchaseOrderNumber?: string | null;
  status: string;
  createdAt: string;
  buyingOrganization: { name: string; buyingOrganizationCode?: string | null };
  sellingOrganization: {
    name: string;
    sellingOrganizationCode?: string | null;
  };
  parentOrder?: { id: string } | null;
  isBrokered: boolean;
  buyer: { firstName: string; lastName: string; externalId?: string | null };
  currency?: string | null;
  shippingLocation?: { name: string; code?: string | null } | null;
  deliveryAddress?: {
    addressLine1?: string | null;
    city?: string | null;
    region?: string | null;
  } | null;
  items: Array<{
    quantity: Pick<Quantity, 'value' | 'unit'>;
    effectivePrice?: PricedAmount | null;
    quantityStored?: Pick<Quantity, 'value'> | null;
    pricePerPiece?: PricedAmount | null;
    platformProductsLengths?: {
      platformProduct?: { name: string; code?: string | null } | null;
    } | null;
  }>;
};

function distinctProductsOf(order: OrderListOrder): OrderCardProduct[] {
  const distinct = new Map<string, OrderCardProduct>();
  for (const item of order.items) {
    const product = item.platformProductsLengths?.platformProduct;
    if (product) {
      distinct.set(formatNameWithCode(product.name, product.code), {
        name: product.name,
        code: product.code,
      });
    }
  }
  return [...distinct.values()];
}

function destinationOf(order: OrderListOrder): string | null {
  const address = order.deliveryAddress;
  if (!address) {
    return null;
  }
  const parts = [address.addressLine1, address.city, address.region].filter(
    (part): part is string => Boolean(part)
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

type OrderListProps = {
  orders: OrderListOrder[];
  noOrderMessage: string;
  noOrderTitle: string;
  perspective: TradePerspective;
  totalCount: number;
  filtered: boolean;
  isLoadingMore?: boolean;
  sentinelRef?: Ref<HTMLDivElement>;
  loadMoreFailed?: boolean;
  onRetryLoadMore?: () => void;
};

export function OrderList({
  orders,
  noOrderMessage,
  noOrderTitle,
  perspective,
  totalCount,
  filtered,
  isLoadingMore = false,
  sentinelRef,
  loadMoreFailed = false,
  onRetryLoadMore = () => {},
}: Readonly<OrderListProps>) {
  const navigate = useNavigate();
  const { t } = useTranslation(['order', 'general']);
  const {
    copyOrderToCart,
    pendingOrderIds,
    skippedItems,
    closeSkippedItemsModal,
  } = useCopyOrderToCartAction();

  if (orders.length === 0)
    return (
      <div className="flex flex-col gap-4">
        <OrdersCountLine shown={0} total={totalCount} filtered={filtered} />
        <EmptyState
          icon={<Package size={48} />}
          title={filtered ? t('order.filter.no_matches_title') : noOrderTitle}
          message={
            filtered ? t('order.filter.no_matches_message') : noOrderMessage
          }
        />
      </div>
    );

  const openOrder = (orderId: string) => navigate(`/orders/view/${orderId}`);

  return (
    <div className="space-y-4 mt-8">
      <OrdersCountLine
        shown={orders.length}
        total={totalCount}
        filtered={filtered}
      />
      {orders.map((order) => {
        if (perspective === 'BUYING') {
          const ineligibleForCopy =
            order.parentOrder != null || order.isBrokered;
          return (
            <OrderCard
              key={order.id}
              id={formatOrderDisplayId(order)}
              status={order.status}
              orderDate={order.createdAt}
              sellingOrganizationName={sellerLabel(order.sellingOrganization)}
              itemCount={order.items.length}
              totalAmount={calculateOrderTotal(order)}
              currency={
                order.currency ?? order.items[0]?.effectivePrice?.currency
              }
              parentOrder={order.parentOrder}
              products={distinctProductsOf(order)}
              plant={order.shippingLocation ?? null}
              deliveryLocation={destinationOf(order)}
              createdBy={{
                name: `${order.buyer.firstName} ${order.buyer.lastName}`,
                email: order.buyer.externalId,
              }}
              onClick={() => openOrder(order.id)}
              onCopyToCart={() => copyOrderToCart(order.id)}
              isCopyingToCart={pendingOrderIds.has(order.id)}
              copyToCartDisabled={
                ineligibleForCopy ||
                (pendingOrderIds.size > 0 && !pendingOrderIds.has(order.id))
              }
              copyToCartDisabledReason={
                ineligibleForCopy
                  ? t('order.copy_to_cart_ineligible_reason')
                  : undefined
              }
            />
          );
        }
        return (
          <SellerOrderCard
            key={order.id}
            id={formatOrderDisplayId(order)}
            status={order.status}
            orderDate={order.createdAt}
            buyingOrganizationName={formatNameWithCode(
              order.buyingOrganization.name,
              order.buyingOrganization.buyingOrganizationCode
            )}
            itemCount={order.items.length}
            totalAmount={calculateOrderTotal(order)}
            currency={
              order.currency ?? order.items[0]?.effectivePrice?.currency
            }
            products={distinctProductsOf(order)}
            plant={order.shippingLocation ?? null}
            deliveryLocation={destinationOf(order)}
            onClick={() => openOrder(order.id)}
          />
        );
      })}
      <CopySkippedItemsModal
        skippedItems={skippedItems}
        onOkay={closeSkippedItemsModal}
      />
      {isLoadingMore && (
        <div
          className="py-4 text-center text-neut-sec text-sm"
          data-testid="order-list-loading-more"
        >
          {t('order.filter.loading_more')}
        </div>
      )}
      {loadMoreFailed && !isLoadingMore && (
        <RetryBanner
          message={t('order.load_more_error')}
          retryLabel={t('general:general.retry')}
          onRetry={onRetryLoadMore}
          testId="order-list-next-page"
          className="justify-center py-4"
        />
      )}
      <div
        ref={sentinelRef}
        className="h-px"
        data-testid="order-list-scroll-sentinel"
      />
    </div>
  );
}
